/** Pointer ownership keeps held controls reliable across multitouch and interruptions. */
export function bindHoldControls(
  buttons: Iterable<HTMLButtonElement>,
  input: { virtualPress(code: string): void; virtualRelease(code: string): void },
  target: Window = window,
): () => void {
  const active = new Map<number, { button: HTMLButtonElement; code: string }>()
  const release = (pointerId: number): void => {
    const entry = active.get(pointerId)
    if (!entry) return
    active.delete(pointerId)
    const remaining = [...active.values()]
    if (!remaining.some(({ button }) => button === entry.button)) entry.button.classList.remove('active')
    if (!remaining.some(({ code }) => code === entry.code)) input.virtualRelease(entry.code)
    if (entry.button.hasPointerCapture(pointerId)) entry.button.releasePointerCapture(pointerId)
  }
  const releaseAll = (): void => {
    for (const pointerId of [...active.keys()]) release(pointerId)
  }
  for (const button of buttons) {
    const code = button.dataset.key
    if (!code) continue
    button.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || button.disabled || active.has(event.pointerId)) return
      event.preventDefault()
      const alreadyHeld = [...active.values()].some((entry) => entry.code === code)
      active.set(event.pointerId, { button, code })
      button.classList.add('active')
      button.setPointerCapture(event.pointerId)
      if (!alreadyHeld) input.virtualPress(code)
    })
    const end = (event: PointerEvent): void => release(event.pointerId)
    button.addEventListener('pointerup', end)
    button.addEventListener('pointercancel', end)
    button.addEventListener('lostpointercapture', end)
    button.addEventListener('click', (event) => {
      event.preventDefault()
      // Keyboard and assistive-technology activation has no preceding pointerdown.
      if (event.detail === 0 && ![...active.values()].some((entry) => entry.code === code)) {
        input.virtualPress(code)
        input.virtualRelease(code)
      }
    })
  }
  target.addEventListener('pointerup', (event) => release(event.pointerId))
  target.addEventListener('pointercancel', (event) => release(event.pointerId))
  target.addEventListener('blur', releaseAll)
  target.document.addEventListener('visibilitychange', () => {
    if (target.document.hidden) releaseAll()
  })
  return releaseAll
}
